import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Field, FormProvider, observer, useForm } from "@formily/react";
import CenteredModal from "components/core/basic/CenteredModal";
import { Col, Row } from "react-bootstrap";
import { Button } from "../../../../components/core/basic/Button";
import Loading from "../../../../components/core/blocks/Loading";
import { CurrencyInput } from "../../../../components/fields/formily/NumberInput";
import { useFormily } from "../../../../hooks/useFormily";
import type { PricingPlan } from "../../../../zodios/apiDefinitions/pricing";
import { useCharmPrice } from "../../api/useCharmPrice";
import { usePricingPlan } from "../../api/usePricingPlan";
import { usePricingPlanUpdate } from "../../api/usePricingPlanUpdate";
import { PlanSliderFormily } from "../PricingSlider";
import { DevErrorDisplay } from "components/core/blocks/DevErrorDisplay";
interface Props {
  initialValues: PricingPlan;
}

export const QuickPlanConfigModal = NiceModal.create(({ planId }: { planId: number }) => {
  const modal = useModal();
  const planQuery = usePricingPlan({ id: planId }, { refetchOnMount: true });

  return (
    <CenteredModal show={modal.visible} title={planQuery.data?.plan_name ?? ""} closeModal={() => modal.remove()}>
      {planQuery.isLoading || !planQuery.isFetchedAfterMount ?
        <Loading />
      : planQuery.isSuccess ?
        planQuery.data.plan_type === "competitor" ?
          "Competitor plans are not supported for quick config"
        : <QuickPlanConfigForm initialValues={planQuery.data} />
      : <DevErrorDisplay userMessage="There was an error loading the plan" error={planQuery.error} />}
    </CenteredModal>
  );
});

function QuickPlanConfigForm({ initialValues }: Props) {
  const form = useFormily({ initialValues });
  return (
    <FormProvider form={form}>
      <QuickPlanConfigFormBody />
    </FormProvider>
  );
}

const QuickPlanConfigFormBody = observer(() => {
  const form = useForm<Props["initialValues"]>();

  const minCharmQuery = useCharmPrice({ price: form.values.min_price });
  const maxCharmQuery = useCharmPrice({ price: form.values.max_price });
  const updatePlanMutation = usePricingPlanUpdate();
  const modal = useModal();
  return (
    <Col className={"px-5"}>
      <Row>
        <Col>
          <Field
            name="min_price"
            title={"Minimum Price"}
            component={[CurrencyInput]}
            validator={{
              validator: (value) => {
                if (!value) {
                  return "Minimum price is required";
                }
                if (value > form.values.max_price) {
                  return "Minimum price cannot be greater than maximum price";
                }

                if (value < 0) {
                  return "Minimum price cannot be less than 0";
                }
                return true;
              },
            }}
          />
          {`The charmed minimum price will be ${minCharmQuery.data?.price ?? "..."}`}
        </Col>
        <Col>
          <Field
            name="max_price"
            title={"Maximum Price"}
            component={[CurrencyInput]}
            validator={{
              validator: (value) => {
                if (!value) {
                  return "Maximum price is required";
                }
                if (value < form.values.min_price) {
                  return "Maximum price cannot be less than minimum price";
                }

                if (value < 0) {
                  return "Maximum price cannot be less than 0";
                }
                return true;
              },
            }}
          />
          {`The charmed maximum price will be ${maxCharmQuery.data?.price ?? "..."}`}
        </Col>
      </Row>
      {form.values.is_auto && (
        <Row className={"mt-3"} style={{ height: 75 }}>
          <Field name="configured_slider_value" component={[PlanSliderFormily]} />
        </Row>
      )}
      <Row className={"mt-5"}>
        <Button
          isDisabled={form.invalid || form.submitting}
          onPress={() => {
            void form.submit(async (values) => {
              await updatePlanMutation.mutateAsync(
                {
                  plan_id: values.id,
                  data: values,
                },
                { onSuccess: () => modal.remove() },
              );
            });
          }}
        >
          Submit
        </Button>
      </Row>
    </Col>
  );
});
